import React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FormikProps, Formik } from 'formik';
import { toFormikValidate } from 'zod-formik-adapter';
import omit from 'lodash/omit';
import parsePhoneNumber from 'libphonenumber-js';

import { useIsSignedIn, useOauthSignup, useSignup } from '@front/auth/src/authentication-context';

import useModal from '@front/common/src/hooks/use-modal';
import { validateAddress } from '@front/common/src/forms/validators/address';
import { isChildcareRecurring as isChildcareRecurringUtil } from '@front/common/src/utils/basket';
import { OAuthLoginData, Salutation, SignupUser, User } from '@front/common/src/types/commons';
import { useUserEmailExists } from '@front/common/src/queries/user-queries';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { useConfig } from '@front/common/src/providers/config-provider';
import { useVariant } from '@front/common/src/providers/experiment-provider';
import { CUSTOMER_AUTOCOMPLETE_EXCLUDED_TYPES } from '@front/common/src/constants/woosmap';
import {
  getBasketAddress,
  getBasketContent,
  getBasketWeeklyRecurrence,
  getUniverseRef,
} from '@front/common/src/ducks/customer/basket-selectors';
import { isCleaningReference } from '@front/common/src/utils/universes';
import { getDialCode } from '@front/common/src/forms/validators/phone/country-codes-config';
import { Country } from '@front/common/src/types/country';
import { emailSchema } from '@front/common/src/forms/validators/email-schema';
import { firstNameSchema } from '@front/common/src/forms/validators/first-name-schema';
import { lastNameSchema } from '@front/common/src/forms/validators/last-name-schema';

import {
  getInitialValues,
  getSignupFormSchema,
  refineAddressType,
  SignupFormType,
} from '@front/customer/src/screens/signup/form-schema';
import { getScreenTitle as getScreenTitleForChildcareAndCleaning } from '@front/customer/src/screens/signup-choice/utils';

import { translate } from '@front/i18n';

import WoosmapAutocomplete from '@front/web-ui/src/components/autocomplete-address/woosmap-autocomplete';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import Button from '@front/web-ui/src/components/button/button';
import GenderSelector from '@front/web-ui/src/components/gender-selector/gender-selector';
import Screen from '@front/web-ui/src/components/screen/screen';
import Alert from '@front/web-ui/src/components/alert/alert';
import { ValidatedPassword } from '@front/web-ui/src/components/input-password/validated-password';
import Input from '@front/web-ui/src/components/input/input';
import InputPhone from '@front/web-ui/src/components/input-phone/input-phone';

import { useAppSelector } from '../../store';
import { logWoosmapErrorIncompleteAddress, trackSignInEvent } from '../../utils/tracking';
import appConfig from '../../utils/config';

import TermsOfService from './terms-of-service';
import EmailTakenAlertModal from './common/email-taken-alert-modal';
import { Container, ErrorContainer, StyledForm } from './common/styles';
import AlreadySubscribed from './already-subscribed';
import useSignupNavigation from './use-signup-navigation';

const t = translate('screens.signup');

const TextContainer = styled.div`
  margin-bottom: 20px;
`;

type GetScreenTitleArgs = {
  isChildcareRecurring: boolean;
  showAllFields: boolean;
  variantForTitle?: string;
  withSocialLogin: boolean;
};

const getScreenTitle = ({
  isChildcareRecurring,
  showAllFields,
  variantForTitle,
  withSocialLogin,
}: GetScreenTitleArgs) => {
  if (withSocialLogin) {
    return t(
      `.titles.${showAllFields ? 'oauth_information_to_check_and_complete' : 'oauth_information_to_complete'}`,
    );
  }
  return getScreenTitleForChildcareAndCleaning({ variantForTitle, isChildcareRecurring });
};

export const Signup = () => {
  const isSignedIn = useIsSignedIn();
  const location = useLocation();
  const config = useConfig();
  const basketAddress = useAppSelector(getBasketAddress);
  const universe = useAppSelector(getUniverseRef);
  const basketContent = useAppSelector(getBasketContent);

  const isChildcareRecurring = isChildcareRecurringUtil(basketContent);
  const { partialUser, omniAuthData }: { partialUser?: Partial<User>; omniAuthData?: OAuthLoginData } =
    location.state || {};
  const isWithSocialLogin = !!partialUser && !!omniAuthData;
  const { mutate: checkEmailTaken, isLoading: emailCheckIsLoading } = useUserEmailExists();
  const { onAccountCreated, openLoginScreen } = useSignupNavigation(isSignedIn);
  const { signup, signupError, isSignupLoading } = useSignup({
    onSuccess: onAccountCreated,
  });
  const { oauthSignupError, oauthSignup, isOauthSignupLoading } = useOauthSignup({
    onSuccess: onAccountCreated,
  });
  const weeklyRecurrence = useAppSelector(getBasketWeeklyRecurrence);

  const showAllFields =
    !partialUser ||
    !emailSchema.safeParse(partialUser.email).success ||
    !firstNameSchema.safeParse(partialUser.first_name).success ||
    !lastNameSchema.safeParse(partialUser.last_name).success;

  const [emailTakenModalVisible, openEmailTakenModal, closeEmailTakenModal] = useModal();

  const countryCode = partialUser?.country_code || getCurrentCountry();
  const dialCode = getDialCode(countryCode) || '';

  const initialValues = getInitialValues({ partialUser, countryCode, dialCode });

  const variantForTitle = useVariant('cst-cleaning-fr-signup-new-title');

  const onSubmit = (values: SignupFormType) => {
    trackSignInEvent({
      eventType: 'CreateAccountButtonClick',
      path: location.pathname,
      universe,
    });

    const address = basketAddress || { ...values.address, country_code: countryCode };
    const finalValues = omit(values, [
      'userInputAddress',
      'addressError',
      'phoneNumber',
      'countryCode',
      'dialCode',
      'address',
    ]);
    const phoneCountryCode = values.countryCode;

    const userData: SignupUser & {
      cleaning_frequency_at_registration?: number;
    } = {
      ...address,
      ...finalValues,
      salutation: finalValues.salutation as Salutation,
      // the phone number is required so the value is defined here (that why we use the "!")
      phone: parsePhoneNumber(values.phoneNumber, phoneCountryCode as Country)?.number!,
      universe_creation: universe,
      country_code: getCurrentCountry(),
    };

    // the user can signup on the location selection screen and in this case the weeklyRecurrence is null
    if (isCleaningReference(universe) && Number.isFinite(weeklyRecurrence)) {
      userData.cleaning_frequency_at_registration = weeklyRecurrence!;
    }

    checkEmailTaken(values.email, {
      onSuccess: (isEmailTaken) => {
        if (isEmailTaken) {
          openEmailTakenModal();
        } else if (isWithSocialLogin) {
          oauthSignup({ omniAuthData, userData });
        } else {
          signup(userData);
        }
      },
    });
  };

  return (
    <Screen
      title={getScreenTitle({
        isChildcareRecurring,
        showAllFields,
        variantForTitle: variantForTitle?.value,
        withSocialLogin: isWithSocialLogin,
      })}
    >
      <EmailTakenAlertModal
        closeModal={closeEmailTakenModal}
        isOpen={emailTakenModalVisible}
        openLoginScreen={openLoginScreen}
      />
      <Container>
        {isChildcareRecurring && !isWithSocialLogin && (
          <TextContainer>
            <Text>{t('.subtitle_childcare')}</Text>
          </TextContainer>
        )}
        <ErrorContainer>
          {signupError && <Alert type='error' content={signupError} />}
          {oauthSignupError && <Alert type='error' content={oauthSignupError} />}
        </ErrorContainer>
        <Formik<SignupFormType>
          onSubmit={onSubmit}
          initialValues={initialValues}
          validate={toFormikValidate(getSignupFormSchema(isWithSocialLogin, basketAddress))}
        >
          {({
            errors: formErrors,
            handleChange,
            handleSubmit,
            isValid,
            setFieldError,
            setFieldTouched,
            setFieldValue,
            touched,
            values,
          }: FormikProps<SignupFormType>) => (
            <StyledForm onSubmit={handleSubmit}>
              <GenderSelector
                error={touched.salutation && formErrors.salutation ? formErrors.salutation : ''}
                onBlur={() => setFieldTouched('salutation', true)}
                onChange={handleChange('salutation')}
                value={values.salutation as Salutation}
              />
              {showAllFields && (
                <Input
                  error={touched.first_name && formErrors.first_name ? formErrors.first_name : ''}
                  id='formSignupFirstName'
                  label={t('inputs.first_name.placeholder')}
                  name='first_name'
                  onBlur={() => setFieldTouched('first_name', true)}
                  onChange={handleChange('first_name')}
                  value={values.first_name}
                />
              )}
              {showAllFields && (
                <Input
                  error={touched.last_name && formErrors.last_name ? formErrors.last_name : ''}
                  id='formSignupLastName'
                  label={t('inputs.last_name.placeholder')}
                  name='last_name'
                  onBlur={() => setFieldTouched('last_name', true)}
                  onChange={handleChange('last_name')}
                  value={values.last_name}
                />
              )}
              <InputPhone
                error={touched.phoneNumber && formErrors.phoneNumber ? formErrors.phoneNumber : ''}
                id='phone'
                label={t('inputs.phone.placeholder')}
                message={isChildcareRecurring ? '' : t('.address.phone_hint')}
                name='phone'
                onBlur={() => setFieldTouched('phoneNumber', true)}
                onCountryCodeChange={handleChange('countryCode')}
                onDialCodeChange={handleChange('dialCode')}
                onPhoneNumberChange={handleChange('phoneNumber')}
                phoneNumber={values.phoneNumber}
              />
              {!validateAddress(basketAddress) && (
                <WoosmapAutocomplete
                  address={refineAddressType(values.address)}
                  apiKey={appConfig.WOOSMAP_API_KEY}
                  error={touched.address && formErrors.addressError ? formErrors.addressError : ''}
                  excludedTypes={CUSTOMER_AUTOCOMPLETE_EXCLUDED_TYPES}
                  id='address'
                  label={t('inputs.address.label')}
                  onBlur={() => setFieldTouched('address', true)}
                  onChange={setFieldValue}
                  onError={setFieldError}
                  onIncompleteAddress={() => logWoosmapErrorIncompleteAddress()}
                  userInputAddress={values.userInputAddress}
                />
              )}
              {showAllFields && (
                <Input
                  error={touched.email && formErrors.email ? formErrors.email : ''}
                  id='email'
                  label={t('inputs.email.placeholder')}
                  name='email'
                  onBlur={() => setFieldTouched('email', true)}
                  onChange={handleChange('email')}
                  type='email'
                  value={values.email}
                />
              )}
              {!isWithSocialLogin && (
                <ValidatedPassword
                  error={touched.password && formErrors.password ? formErrors.password : ''}
                  id='password'
                  label={t('inputs.password.placeholder')}
                  name='password'
                  onBlur={() => setFieldTouched('password', true)}
                  onChange={handleChange('password')}
                  value={values.password}
                />
              )}
              <TermsOfService routes={config.routes} universe={universe} />
              <Button
                type='submit'
                size='lg'
                isLoading={isOauthSignupLoading || isSignupLoading || emailCheckIsLoading}
                isDisabled={!isValid}
                hasFullWidth={true}
              >
                {t('.submit')}
              </Button>
              <AlreadySubscribed universe={universe} openLoginScreen={openLoginScreen} />
            </StyledForm>
          )}
        </Formik>
      </Container>
    </Screen>
  );
};

export default Signup;
